import type { IconList } from '@snowit/icetypes';
import type { Transition } from 'framer-motion';
import type { INotification } from './types';

import { Icon, Markdown, Typo } from '@snowit/ui';
import { m } from '@snowit/ui/utils/Motion';

import cx from 'classnames';
import s from './Notification.module.scss';

const Notification = (props: INotification) => {
  const { theme = 'default', title, message, onClose, onClick, position } = props;

  const getIcon = (): IconList => {
    switch (theme) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'ok':
        return 'check_circle';
      case 'marketing':
        return 'bell_ring_animation';
      default:
        return 'info_circle';
    }
  };

  const animation = {
    variants: {
      initial: {
        opacity: 0,
        scale: 0.8,
        x: position === 'topLeft' || position === 'bottomLeft' ? -300 : 300,
      },
      animate: {
        opacity: 1,
        scale: 1,
        x: 0,
      },
      exit: {
        opacity: 0,
        scale: 0.8,
        x: position === 'topLeft' || position === 'bottomLeft' ? -300 : 300,
      },
    },
    transition: {
      type: 'spring',
      stiffness: 500,
      damping: 40,
    } satisfies Transition,
  };

  const handleClose = (e) => {
    e.stopPropagation();
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  const handleClick = (e) => {
    handleClose(e);
    if (typeof onClick === 'function') {
      onClick();
    }
  };

  const renderClose = () => {
    if (!onClose) return null;
    if (theme !== 'marketing') return null;
    return <Icon className={s.close} name={'close'} onClick={handleClose} />;
  };

  const renderIcon = () => {
    return (
      <div className={s.wrapIcon} onClick={handleClick}>
        <Icon className={s.icon} name={getIcon()} />
      </div>
    );
  };

  const renderTitle = () => {
    if (!title) return null;
    return <Typo text={title} tag={'h2'} size={'df'} weight={'medium'} color={'text'} balancer />;
  };

  const renderMessage = () => {
    if (!message) return null;
    return <Markdown source={message} baseSize={'md'} />;
  };

  return (
    <m.div
      className={cx(s.wrapper, s[theme], {
        [s.hasClose]: onClose && theme === 'marketing',
      })}
      initial='initial'
      animate='animate'
      exit='exit'
      variants={animation.variants}
      transition={animation.transition}
    >
      {renderIcon()}
      <div className={s.wrapText} onClick={handleClick}>
        {renderTitle()}
        {renderMessage()}
      </div>
      {renderClose()}
    </m.div>
  );
};

export default Notification;
